import { Instagram, ArrowUpRight, Feather } from "lucide-react";
import Reveal from "./Reveal";
import { usePosts } from "@/hooks/usePosts";
import { useLang } from "@/i18n/LangContext";

const IG_URL = "https://www.instagram.com/alagrandejavier";

const IG_TEXT = {
  es: { latest: "Última publicación en Instagram" },
  en: { latest: "Latest Instagram post" },
  pt: { latest: "Última publicação no Instagram" },
  fr: { latest: "Dernière publication Instagram" },
};

const InstagramFeed = () => {
  const { posts } = usePosts();
  const { t, lang } = useLang();
  const shown = posts.slice(0, 4);
  const TXT = IG_TEXT[lang] ?? IG_TEXT.es;

  return (
    <section id="instagram" data-testid="instagram-section" className="relative py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-14 max-w-2xl">
          <p className="font-code text-xs tracking-[0.35em] uppercase text-[#C5A059] mb-4">
            {t("instagram.overline")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] tracking-tight">
            {t("instagram.headingA")} <span className="italic text-[#D4AF37]">{t("instagram.headingB")}</span>
          </h2>
          <p className="mt-5 text-[#A0A0A5] leading-relaxed">
            {t("instagram.intro")}
          </p>
          <a
            data-testid="instagram-follow-cta"
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-3 px-7 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
          >
            <Instagram className="w-4 h-4" />
            {t("instagram.follow")}
          </a>
        </Reveal>

        <div className="grid lg:grid-cols-[minmax(0,400px)_1fr] gap-8 items-start">
          <Reveal>
            <div className="border border-[#D4AF37]/25 bg-[#141417]" data-testid="instagram-embed-frame">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#F4F1EA]/10">
                <span className="flex items-center gap-2.5 font-code text-[11px] tracking-[0.25em] uppercase text-[#C5A059]">
                  <Instagram className="w-4 h-4 text-[#D4AF37]" />
                  {TXT.latest}
                </span>
                <a
                  href={IG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="text-[#8E8E93] hover:text-[#D4AF37] transition-colors duration-300"
                >
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-featured-photo"
                className="group relative block overflow-hidden"
              >
                <img
                  src="/instagram-episodio-23.jpg"
                  alt="Episodio 23 · El Silencio — Yosel esperaba una orden. Chávez guardó silencio. · @alagrandejavier en Instagram"
                  className="w-full h-auto block transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0E]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </a>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-6" data-testid="instagram-grid">
            {shown.length === 0 &&
              [1, 2].map((n) => (
                <div
                  key={n}
                  data-testid={`instagram-placeholder-${n}`}
                  className="border border-dashed border-[#F4F1EA]/15 bg-[#141417]/40 aspect-square flex flex-col items-center justify-center text-center p-8"
                >
                  <Feather className="w-6 h-6 text-[#C5A059] mb-4" />
                  <p className="font-display text-xl text-[#8E8E93]">{t("instagram.placeholder")}</p>
                  <p className="mt-2 font-code text-[10px] tracking-[0.3em] uppercase text-[#5a5a5e]">
                    {t("instagram.placeholderSub")}
                  </p>
                </div>
              ))}
            {shown.map((post) => (
              <a
                key={post.id}
                data-testid={`instagram-card-${post.id}`}
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-[#F4F1EA]/10 hover:border-[#D4AF37]/40 bg-[#141417] aspect-square p-7 flex flex-col transition-colors duration-500"
              >
                <p className="font-code text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">
                  {post.date}
                </p>
                <h3 className="mt-4 font-display text-xl sm:text-2xl text-[#F4F1EA] leading-snug group-hover:text-[#E5C158] transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-[#A0A0A5] leading-relaxed line-clamp-4 flex-1">
                  {post.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]">
                  <Instagram className="w-3.5 h-3.5" />
                  {t("instagram.view")}
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
